import React, { ComponentType, PropsWithChildren, ReactNode } from 'react';

import styled from 'styled-components';
import { HotelDetailAnnouncementViewModel, RoomViewModel } from '@api/index';
import { Available, AvailableLimited } from '@assets/icons';
import Markdown from '@components/utils/Markdown';
import { useDevice } from '@contexts/device';
import Tooltip from '@components/ui/Tooltip/next';

import {
  FeatureGroupHeader,
  FeatureLegend,
  Feature,
  FeatureName,
  FeatureGroupContainer,
  FeatureGroupTitle,
  FeatureGroupDescription,
  FeatureListContainer,
  CustomShowMoreText,
  RoomDimension,
  Bullet,
} from './styled';
import { HotelFeatureGroup, RoomFeaturesMeta, Entity } from './types';

export const FeatureLegendSection = () => (
  <FeatureGroupHeader>
    <span />
    <FeatureLegend>
      <Feature>
        <Available />
        <FeatureName>Ücretsiz</FeatureName>
      </Feature>
      <Feature>
        <AvailableLimited />
        <FeatureName>Ücretli</FeatureName>
      </Feature>
    </FeatureLegend>
  </FeatureGroupHeader>
);

const ShowMoreLink = styled.span`
  text-decoration: underline;
`;
type FeatureGroupProps = {
  features: HotelFeatureGroup['features'];
  Container?: ComponentType<
    PropsWithChildren<
      PropsWithChildren<PropsWithChildren<PropsWithChildren<unknown>>>
    >
  >;
  Header?: ComponentType<
    PropsWithChildren<
      PropsWithChildren<PropsWithChildren<PropsWithChildren<unknown>>>
    >
  >;
  name?: string;
  renderDescription: (description?: string) => ReactNode;
  description?: string;
  roomDescription?: string;
  entityType: NonNullable<Entity>;
  featureId?: number;
};
const RoomDescription = styled.div``;
export const FeatureGroup = ({
  name,
  features,
  Container = FeatureGroupContainer,
  Header = FeatureGroupHeader,
  description,
  entityType,
  roomDescription,
  renderDescription,
  featureId,
}: FeatureGroupProps) => {
  return (
    <Container>
      <Header>
        <FeatureGroupTitle>{name}</FeatureGroupTitle>
      </Header>
      <RoomDescription>
        <Markdown text={roomDescription} />
      </RoomDescription>
      {renderDescription(description)}
      <FeatureList
        featureId={featureId}
        features={features}
        entityType={entityType}
      />
    </Container>
  );
};
type AnnouncementsGroupProps = {
  title?: string;
  description?: string;
  Container?: ComponentType<
    PropsWithChildren<
      PropsWithChildren<PropsWithChildren<PropsWithChildren<unknown>>>
    >
  >;
  renderDescription: (description?: string) => ReactNode;
  Header?: ComponentType<
    PropsWithChildren<
      PropsWithChildren<PropsWithChildren<PropsWithChildren<unknown>>>
    >
  >;
};
export const AnnouncementsGroup = ({
  title,
  description,
  Container = FeatureGroupContainer,
  renderDescription,
  Header = FeatureGroupHeader,
}: AnnouncementsGroupProps) => {
  return (
    <Container>
      <Header>
        <FeatureGroupTitle>{title}</FeatureGroupTitle>
      </Header>
      {renderDescription(description)}
    </Container>
  );
};

type FeatureListProps = Pick<HotelFeatureGroup, 'features'> & {
  entityType: NonNullable<Entity>;
  featureId?: number;
};

export const FeatureList = ({
  features,
  entityType,
  featureId,
}: FeatureListProps) => {
  const { isMobile } = useDevice();

  return (
    <FeatureListContainer entityType={entityType}>
      {features?.map((feat) => (
        <Feature key={feat.name}>
          {featureId === 17 || featureId === 21 ? null : feat.hasCharged ? (
            <AvailableLimited />
          ) : (
            <Available />
          )}

          <Tooltip
            overlayInnerStyle={{
              maxWidth: isMobile ? 260 : 400,
              lineHeight: 1.43,
              textAlign: 'left',
            }}
            placement='top'
            title={feat.name}
          >
            <FeatureName>{feat.name}</FeatureName>
          </Tooltip>
        </Feature>
      ))}
    </FeatureListContainer>
  );
};

type WrapperProps = {
  Container: ComponentType<
    PropsWithChildren<
      PropsWithChildren<PropsWithChildren<PropsWithChildren<unknown>>>
    >
  >;
  Header: ComponentType<
    PropsWithChildren<
      PropsWithChildren<PropsWithChildren<PropsWithChildren<unknown>>>
    >
  >;
};

const renderHotelFeatureDescription = (description?: string) => {
  {
    return description ? (
      <CustomShowMoreText
        lines={2}
        more={<ShowMoreLink>Devamını Oku</ShowMoreLink>}
        less=''
        expanded={false}
        width={450}
      >
        <FeatureGroupDescription
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </CustomShowMoreText>
    ) : null;
  }
};
export const HotelAnnouncements = ({
  Container,
  Header,
  title,
  description,
}: HotelDetailAnnouncementViewModel & WrapperProps) => {
  return (
    <>
      <AnnouncementsGroup
        title={title}
        description={description}
        Container={Container}
        Header={Header}
        renderDescription={renderHotelFeatureDescription}
      />
    </>
  );
};

const getAreaDescription = ({ minAreaSize, maxAreaSize }: RoomViewModel) => {
  if (minAreaSize === undefined && maxAreaSize === undefined) return '';
  if (minAreaSize != maxAreaSize) {
    return `${minAreaSize}-${maxAreaSize}`;
  }
  return `${minAreaSize || maxAreaSize}`;
};

const renderRoomFeatureDescription = (areaDescription?: string) =>
  areaDescription ? (
    <RoomDimension>
      <Bullet />
      <span>
        Oda Büyüklüğü: {areaDescription} m<sup>2</sup>
      </span>
    </RoomDimension>
  ) : null;

export const RoomFeatures = ({
  features,
  room,
}: Pick<RoomFeaturesMeta, 'features'> & { room?: RoomViewModel }) => {
  const description = room ? getAreaDescription(room) : '';
  return (
    <FeatureGroup
      features={features}
      name='Oda Özellikleri'
      renderDescription={renderRoomFeatureDescription}
      description={description}
      roomDescription={room?.description}
      entityType='room'
    />
  );
};
